import { useEffect } from 'react';
import './IngredientDetailModal.css';
import CapacityMark from './CapacityMark';
import { SHOPIFY_STORE_URL } from '../config';
import {
  capacityTierDescription,
  capacityTierLabel,
} from '../utils/capacityGuidance';
import { displayIngredientName } from '../utils/displayIngredientName';

const CAFFEINE_LABEL = {
  none: 'Caffeine free',
  low: 'Low caffeine',
  medium: 'Moderate caffeine',
  high: 'High caffeine',
};

function capitalizeSentenceStart(text) {
  if (!text || typeof text !== 'string') return '';
  const t = text.trim();
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function bulkProductUrl(shopifyHandle) {
  const handle = typeof shopifyHandle === 'string' ? shopifyHandle.trim() : '';
  if (!handle) return null;
  const base = SHOPIFY_STORE_URL.replace(/\/$/, '');
  return `${base}/products/${encodeURIComponent(handle)}`;
}

function IngredientDetailModal({ ingredient, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const displayName = displayIngredientName(ingredient.name, ingredient.quality);
  const productUrl = bulkProductUrl(ingredient.shopify_handle);

  return (
    <div className="ing-modal-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-label={displayName}>
      <div className="ing-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ing-modal__header">
          <div>
            <h2 className="ing-modal__name">
              {displayName}
              {ingredient.capacity_tier && (
                <CapacityMark tier={ingredient.capacity_tier} interactive={false} />
              )}
            </h2>
            <p className="ing-modal__botanical">{ingredient.botanical_name}</p>
          </div>
          <button className="ing-modal__close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="ing-modal__body">
          {(ingredient.origin_phrase || ingredient.origin_label) && (
            <div className="ing-modal__origin">
              <span className="ing-modal__origin-icon" aria-hidden="true">
                <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M10 17.5s5.5-4.2 5.5-8.75a5.5 5.5 0 1 0-11 0C4.5 13.3 10 17.5 10 17.5Z"
                    stroke="currentColor"
                    strokeWidth="1.35"
                    strokeLinejoin="round"
                  />
                  <circle cx="10" cy="8.75" r="1.75" fill="currentColor" />
                </svg>
              </span>
              <div className="ing-modal__origin-copy">
                <p className="ing-modal__origin-eyebrow">Origin</p>
                <p className="ing-modal__origin-region">
                  {ingredient.origin_phrase
                    || (ingredient.origin_label ? `Sourced from ${ingredient.origin_label}` : '')}
                </p>
              </div>
            </div>
          )}

          {ingredient.taste_profile && (
            <div className="ing-modal__section">
              <h3 className="ing-modal__section-title">Taste</h3>
              <p className="ing-modal__text">
                {Array.isArray(ingredient.taste_profile)
                  ? ingredient.taste_profile.map((t) => t.charAt(0).toUpperCase() + t.slice(1)).join(', ')
                  : ingredient.taste_profile}
              </p>
            </div>
          )}

          {ingredient.caffeine && (
            <div className="ing-modal__section">
              <h3 className="ing-modal__section-title">Caffeine</h3>
              <span className={`ing-modal__caffeine-badge caffeine-${ingredient.caffeine}`}>
                {CAFFEINE_LABEL[ingredient.caffeine] || ingredient.caffeine}
              </span>
            </div>
          )}

          {ingredient.capacity_tier && (
            <div className="ing-modal__section">
              <h3 className="ing-modal__section-title">In your blend</h3>
              <p className="ing-modal__text ing-modal__text--capacity">
                <CapacityMark tier={ingredient.capacity_tier} interactive={false} />
                <span>
                  <strong>{capacityTierLabel(ingredient.capacity_tier)}</strong>
                  {' — '}
                  {capacityTierDescription(ingredient.capacity_tier)}
                </span>
              </p>
            </div>
          )}

          {ingredient.benefits && ingredient.benefits.length > 0 && (
            <div className="ing-modal__section">
              <h3 className="ing-modal__section-title">Benefits</h3>
              <ul className="ing-modal__list">
                {ingredient.benefits.map((b, i) => <li key={i}>{capitalizeSentenceStart(b)}</li>)}
              </ul>
            </div>
          )}

          {ingredient.recommended_daily_max && (
            <div className="ing-modal__section">
              <h3 className="ing-modal__section-title">Recommended intake</h3>
              <p className="ing-modal__text">
                Maximum daily: <strong>{ingredient.recommended_daily_max}g</strong>
              </p>
            </div>
          )}

          {ingredient.stock_note && (
            <div className="ing-modal__section">
              <h3 className="ing-modal__section-title">Availability</h3>
              <p className="ing-modal__text">{ingredient.stock_note}</p>
            </div>
          )}

          {productUrl && (
            <div className="ing-modal__section ing-modal__section--more">
              <h3 className="ing-modal__section-title">Want more detail?</h3>
              <a
                className="ing-modal__bulk-link"
                href={productUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                View {displayName} bulk page
                <span className="ing-modal__bulk-link-arrow" aria-hidden="true">↗</span>
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default IngredientDetailModal;
